import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft,
  CheckCircle2,
  CreditCard,
  Loader2,
  WalletCards,
} from "lucide-react";
import { toast } from "react-toastify";

import {
  checkMadeToMeasureRequestPaymentStatus,
  createMadeToMeasureRequestPayment,
  getMadeToMeasureRequest,
  resetMadeToMeasurePaymentStatus,
  resetMadeToMeasureRequestPayment,
} from "../../actions/madeToMeasureActions";
import { getPaymentConfig } from "../../actions/paymentActions";
import { PAYMENT_PROVIDERS } from "../../constants/paymentConstants";
import Meta from "../../components/Meta";
import {
  selectMadeToMeasurePaymentStatus,
  selectMadeToMeasureRequestDetails,
  selectMadeToMeasureRequestPayment,
} from "../../selectors/madeToMeasureSelectors";
import { selectPaymentConfig } from "../../selectors/paymentSelectors";
import { getSafeUserMessage } from "../../utils/errorMessages";

import "./MadeToMeasureScreen.css";

const MEASUREMENT_FIELDS = [
  ["waist", "Waist"],
  ["hip", "Hip"],
  ["bust", "Bust"],
  ["length", "Length"],
  ["shoulder", "Shoulder"],
  ["sleeve", "Sleeve"],
];

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatCatalogValue(value = "") {
  const text = safeText(value).replace(/[-_]+/g, " ").replace(/\s+/g, " ");

  if (!text) return "";

  return text
    .split(" ")
    .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : ""))
    .join(" ");
}

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: safeText(currency) || "GBP",
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function formatDate(value = "") {
  if (!value) return "Not set";

  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return "Not set";
  }
}

function getId(request = {}) {
  return safeText(request.id || request._id);
}

function getReference(request = {}) {
  const id = getId(request);
  return id ? `MTM-${id.slice(-6).toUpperCase()}` : "MTM request";
}

function getProductName(request = {}) {
  return safeText(request.selectedProduct?.name) || "Custom garment";
}

function getFabricSummary(request = {}) {
  const textile = request.selectedTextile || {};
  const trims = Array.isArray(request.selectedHaberdashery)
    ? request.selectedHaberdashery
    : [];

  return [
    safeText(textile.name),
    safeText(textile.fabricType || textile.materialType || textile.material),
    trims.length ? `${trims.length} trim${trims.length === 1 ? "" : "s"}` : "",
  ]
    .filter(Boolean)
    .map(formatCatalogValue)
    .join(" | ");
}

function getMeasurementsSummary(request = {}) {
  const measurements = request.measurements || {};
  const unit = safeText(measurements.unit || "cm");
  const parts = MEASUREMENT_FIELDS.map(([key, label]) => {
    const value = safeText(measurements[key]);
    return value ? `${label}: ${value} ${unit}` : "";
  }).filter(Boolean);

  return parts.length ? parts.join(" | ") : "Measurements not added yet";
}

function getCheckoutUrl(payload = {}) {
  return (
    payload.hostedCheckoutUrl ||
    payload.hosted_checkout_url ||
    payload.checkoutUrl ||
    payload.approvalUrl ||
    payload.approveUrl ||
    payload.payerActionUrl ||
    payload.nextAction?.url ||
    ""
  );
}

function PaymentOption({
  active,
  disabled,
  icon,
  label,
  meta,
  onClick,
}) {
  return (
    <button
      type="button"
      className={`mtm-pay-option${active ? " is-active" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      <span>{icon}</span>
      <strong>{label}</strong>
      <small>{meta}</small>
    </button>
  );
}

export default function MadeToMeasurePaymentScreen() {
  const { requestId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const detailsState = useSelector(selectMadeToMeasureRequestDetails);
  const paymentState = useSelector(selectMadeToMeasureRequestPayment);
  const statusState = useSelector(selectMadeToMeasurePaymentStatus);
  const paymentConfig = useSelector(selectPaymentConfig);

  const [selectedProvider, setSelectedProvider] = useState(PAYMENT_PROVIDERS.STRIPE);

  const request =
    statusState.request ||
    paymentState.request ||
    detailsState.request ||
    detailsState.enquiry ||
    null;

  const paymentReturn = safeText(searchParams.get("payment"));
  const returnedFromProvider = Boolean(
    paymentReturn ||
      searchParams.get("token") ||
      searchParams.get("checkout_reference"),
  );

  useEffect(() => {
    dispatch(getMadeToMeasureRequest(requestId)).catch((error) => {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not load this request right now. Please try again later.",
        ),
      );
    });
    dispatch(getPaymentConfig()).catch(() => {});

    return () => {
      dispatch(resetMadeToMeasureRequestPayment());
      dispatch(resetMadeToMeasurePaymentStatus());
    };
  }, [dispatch, requestId]);

  useEffect(() => {
    if (!returnedFromProvider || !requestId) return;

    const provider =
      safeText(searchParams.get("provider")) ||
      safeText(request?.paymentMethod || request?.paymentResult?.provider) ||
      "";

    dispatch(
      checkMadeToMeasureRequestPaymentStatus(requestId, {
        provider,
      }),
    )
      .then((result) => {
        if (result?.request?.isPaid || result?.enquiry?.isPaid) {
          toast.success("Payment confirmed.");
        }
      })
      .catch((error) => {
        toast.error(
          getSafeUserMessage(
            error?.message,
            "We could not verify payment status right now. Please try again later.",
          ),
        );
      });
  }, [dispatch, requestId, request?.paymentMethod, request?.paymentResult, returnedFromProvider, searchParams]);

  useEffect(() => {
    if (paymentConfig.sumupEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.SUMUP);
    } else if (paymentConfig.paypalEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.PAYPAL);
    } else if (paymentConfig.stripeEnabled) {
      setSelectedProvider(PAYMENT_PROVIDERS.STRIPE);
    }
  }, [
    paymentConfig.paypalEnabled,
    paymentConfig.stripeEnabled,
    paymentConfig.sumupEnabled,
  ]);

  const paymentOptions = useMemo(
    () =>
      [
        paymentConfig.sumupEnabled
          ? {
              provider: PAYMENT_PROVIDERS.SUMUP,
              label: "SumUp",
              meta: "Hosted checkout",
              icon: <WalletCards size={18} />,
            }
          : null,
        paymentConfig.paypalEnabled
          ? {
              provider: PAYMENT_PROVIDERS.PAYPAL,
              label: "PayPal",
              meta: "PayPal secure checkout",
              icon: <WalletCards size={18} />,
            }
          : null,
        paymentConfig.stripeEnabled
          ? {
              provider: PAYMENT_PROVIDERS.STRIPE,
              label: "Card",
              meta: "Secure card checkout",
              icon: <CreditCard size={18} />,
            }
          : null,
      ].filter(Boolean),
    [
      paymentConfig.paypalEnabled,
      paymentConfig.stripeEnabled,
      paymentConfig.sumupEnabled,
    ],
  );

  const isBusy =
    detailsState.loading ||
    paymentState.loading ||
    statusState.loading ||
    paymentConfig.loading;
  const canPay = Boolean(request?.canPay && !request?.isPaid && paymentOptions.length);
  const quoteCurrency =
    request?.linkedOrder?.currency ||
    request?.paymentResult?.currency ||
    request?.linkedOrder?.payment?.currency ||
    "GBP";
  const orderNumber = safeText(request?.linkedOrder?.orderNumber);

  const handleStartPayment = async () => {
    if (!request || !selectedProvider || !canPay) return;

    try {
      const result = await dispatch(
        createMadeToMeasureRequestPayment(getId(request), {
          provider: selectedProvider,
        }),
      );
      const redirectUrl = getCheckoutUrl(result);

      if (!redirectUrl) {
        throw new Error("The payment provider did not return a checkout URL.");
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not start payment right now. Please try again later.",
        ),
      );
    }
  };

  const handleRefreshStatus = () => {
    dispatch(
      checkMadeToMeasureRequestPaymentStatus(requestId, {
        provider: selectedProvider,
      }),
    ).catch((error) => {
      toast.error(
        getSafeUserMessage(
          error?.message,
          "We could not refresh payment status right now. Please try again later.",
        ),
      );
    });
  };

  return (
    <main className="mtm-page">
      <Meta
        title="Made To Measure Payment | Academy Costumes London"
        description="Pay securely for your made to measure request."
      />

      <section className="mtm-pay">
        <Link className="mtm-nav-button mtm-nav-button--ghost" to="/made-to-measure">
          <ArrowLeft size={16} />
          Back to requests
        </Link>

        {detailsState.loading && !request ? (
          <div className="mtm-state">
            <Loader2 className="mtm-spin" />
            <p>Loading request...</p>
          </div>
        ) : detailsState.error ? (
          <div className="mtm-state">
            <p>{detailsState.error}</p>
          </div>
        ) : request ? (
          <>
            <header className="mtm-pay__head">
              <div>
                <p className="mtm-eyebrow">{getReference(request)}</p>
                <h1>{getProductName(request)}</h1>
                <p>{getFabricSummary(request) || "Fabric to be confirmed"}</p>
              </div>
              <strong>{formatCatalogValue(request.status)}</strong>
            </header>

            {request.isPaid ? (
              <div className="mtm-success mtm-success--stacked">
                <CheckCircle2 size={24} />
                <div>
                  <strong>Payment confirmed</strong>
                  <p>
                    Paid {formatDate(request.paidAt || request.quotePaidAt)}
                    {orderNumber ? ` | Order ${orderNumber}` : ""}
                  </p>
                </div>
              </div>
            ) : statusState.pending ? (
              <div className="mtm-warning">
                <Loader2 size={16} className="mtm-spin" />
                <span>Payment is still pending. Refresh again after SumUp or PayPal updates the checkout.</span>
              </div>
            ) : returnedFromProvider ? (
              <div className="mtm-warning">
                <Loader2 size={16} className="mtm-spin" />
                <span>Checking the real payment status with the server.</span>
              </div>
            ) : null}

            <div className="mtm-pay__layout">
              <section className="mtm-summary mtm-summary--quote">
                <h2>Payment summary</h2>
                <div className="mtm-pay__facts">
                  <div>
                    <span>Reference</span>
                    <strong>{getReference(request)}</strong>
                  </div>
                  <div>
                    <span>Request date</span>
                    <strong>{formatDate(request.createdAt)}</strong>
                  </div>
                  <div>
                    <span>Quote</span>
                    <strong>
                      {request.finalQuotePrice
                        ? formatMoney(request.finalQuotePrice, quoteCurrency)
                        : "Quote pending"}
                    </strong>
                  </div>
                  <div>
                    <span>Linked order</span>
                    <strong>{orderNumber || "Not created yet"}</strong>
                  </div>
                </div>

                <div className="mtm-summary__group">
                  <h3>Measurements</h3>
                  <p>{getMeasurementsSummary(request)}</p>
                </div>

                <div className="mtm-summary__group">
                  <h3>Notes</h3>
                  <p>{safeText(request.notes) || "No notes added."}</p>
                </div>
              </section>

              <section className="mtm-pay__panel">
                <h2>Payment method</h2>

                {!canPay ? (
                  <div className={request.isPaid ? "mtm-success" : "mtm-warning"}>
                    {request.isPaid ? <CheckCircle2 size={18} /> : <CreditCard size={18} />}
                    <span>
                      {request.isPaid
                        ? "This request has already been paid."
                        : request.paymentBlockedReason ||
                          "This request is not ready for payment yet."}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="mtm-pay-options">
                      {paymentOptions.map((option) => (
                        <PaymentOption
                          key={option.provider}
                          active={selectedProvider === option.provider}
                          disabled={isBusy}
                          icon={option.icon}
                          label={option.label}
                          meta={option.meta}
                          onClick={() => setSelectedProvider(option.provider)}
                        />
                      ))}
                    </div>

                    {paymentState.error ? (
                      <p className="mtm-error">{paymentState.error}</p>
                    ) : null}
                    {statusState.error ? (
                      <p className="mtm-error">{statusState.error}</p>
                    ) : null}

                    <div className="mtm-payment-actions">
                      <button
                        type="button"
                        className="mtm-nav-button"
                        onClick={handleRefreshStatus}
                        disabled={isBusy}
                      >
                        Check status
                      </button>
                      <button
                        type="button"
                        className="mtm-nav-button mtm-nav-button--primary"
                        onClick={handleStartPayment}
                        disabled={isBusy || !selectedProvider}
                      >
                        {paymentState.loading ? (
                          <Loader2 size={16} className="mtm-spin" />
                        ) : (
                          <CreditCard size={16} />
                        )}
                        Pay securely
                      </button>
                    </div>
                  </>
                )}
              </section>
            </div>
          </>
        ) : (
          <div className="mtm-state">
            <p>We could not find this made to measure request.</p>
          </div>
        )}
      </section>
    </main>
  );
}
